import { subGet } from './Object';

class EventDetails {
    constructor(public sender:any, public type:string, public data: any) {

    }
}

class Emitter extends require('events') {
    emit(type, ...args) {
        super.emit(type, ...args)
        super.emit('*', type, ...args)
    }
}

interface IInstantiatable {
    new (...args): Object;
}

/**
 * @events change 
 * @events scopeAdded
 * @events scopeChanged
 */
export class ScopedBase<T> extends Emitter {
    protected selectedScope:number|string;
    protected database: { [ key: string ]: T } = {  };
    public itemType:IInstantiatable;
    public itemFactory:Function;

    constructor(itemType?:IInstantiatable, itemFactory?:Function) {
        super();
        if (itemType) {
            this.itemType = itemType;
            this.itemFactory = (...args) => new itemType(...args)
        }
        
        if (itemFactory)
            this.itemFactory = itemFactory;
    }

    protected changed(subEvent, ...args) {
        //this.emit('change', new EventDetails(this, 'change', args));
        this.emit(subEvent, new EventDetails(this, subEvent, args));
    }

    public toJSON():any {
        return this.database[this.selectedScope];
    }

    /**
     * Add new scope with optional default value
     */
    addScope(key:string, defaultValue:T) {
        this.database[key] = defaultValue;
        this[key] = this.database[key];

        this.changed('scopeAdded', {
            scopes: Object.keys(this.database)
        });

        if (!this.selectedScope)
            this.setSelectedScope(key);
    }

    reset() {
        this.database = {};
    }

    clear(scope:number|string = this.selectedScope) {
        scope = scope || this.selectedScope;
        this.database[scope] = this.itemFactory({});
    }

    getSelectedScope() {
        return this.selectedScope;
    }

    setSelectedScope(scope:number|string = this.selectedScope) {
        scope = scope || this.selectedScope;
        if (!this.database[scope]) {
            throw new Error("Scope not found: " + scope);
        }
        this.selectedScope = scope;
        this.changed('scopeChanged', {
            selectedScope: this.selectedScope
        });
    }

    getScope(scope = this.selectedScope) {
        return this.database[scope];
    }
}

/**
 * @events containerAdded 
 */
export class ScopedStorage extends ScopedBase<ScopedContainer<any>|ScopedList<any>|ScopedStorage> {
    protected database: { [ key: string ]: ScopedContainer<any>|ScopedList<any>|ScopedStorage } = {  };

    public toJSON():any {
        let res = {};
        for (let cont of Object.keys(this.database)) {
            res[cont] = this.database[cont];
        }
        return res;
    }

    setSelectedScope(scope:string|number) {
        for (let cont of Object.values(this.database)) {
            cont.setSelectedScope(scope);
        }

        this.selectedScope = scope;
        this.changed('scopeChanged', {
            selectedScope: this.selectedScope
        });
    }

    addContainer(key:string, cont:ScopedContainer<any>|ScopedList<any>) {
        this.database[key] = cont;
        this[key] = cont;
        this.changed('containerAdded', {
            containers: Object.keys(this.database)
        });
        cont.on('*', (type, ...args) => {
            this.emit(type, ...args);
        });
    }

    reset() {
        this.selectedScope = null;
        for (let dbKey of Object.keys(this.database)) {
            this.database[dbKey].reset();
        }
    }

    clear(scope:number|string = this.selectedScope) {
        for (let dbKey of Object.keys(this.database)) {
            this.database[dbKey].clear(scope);
        }
    }

    /*
     * Add new scope with optional default values
     */
    addScope(key:string, defaults:{ [ key: string ]: any } = { }) {
        for (let dbKey of Object.keys(this.database)) {
            this.database[dbKey]
                .set(defaults[dbKey] || [], key);
        }

        this.changed('scopeAdded', {
            scopes: Object.keys(this.database)
        });

        if (!this.selectedScope)
            this.setSelectedScope(key);
    }

    set(newData, scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        for (let key of Object.keys(this.database)) {
            if (newData[key]) {
                this.database[key].set(newData[key], scope);
            }
        }

        if (!this.selectedScope) {
            this.setSelectedScope(scope);
        }
        
        this.changed('itemChanged', {
            scope: this.database[scope]
        });
    }

}

/**
 * Meant to hold single objects fro each scope
 * @events itemChanged
 * @events propertyChanged
 */
export class ScopedContainer<T> extends ScopedBase<T> {
    protected database:{ [ id: string ]: T } = {};

    protected autoScope(obj?:T) {
        return this.selectedScope;
    }

    /**
     * Get the whole value of the scope
     * @param scope (Optional, default: selectedScope)
     */
    get(scope = this.autoScope()):T {
        scope = scope || this.autoScope();
        return this.database[scope];
    }

    /**
     * Set the whole value of the scope
     */
    set(item:T, scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        
        this.database[scope] = item;

        if (!this.selectedScope) {
            this.setSelectedScope(scope);
        }

        this.changed('itemChanged', {
            scope: this.database[scope]
        });
    }

    /**
     * Get the value of a key inside the scope
     */
    getKey(key:string, scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        return this.database[scope][key];
    }

    /**
     * Set the value of a key inside the scope
     */
    setKey(key:string, value:any, scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        this.database[scope][key] = value;
        this.changed('propertyChanged', {
            key: key,
            scope: this.database[scope]
        });
    }
    
    /**
     * Update/set multiple keys of the scope
     */
    update(item:Partial<T>, scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        this.database[scope] = Object.assign({}, this.database[scope], item);
        this.emit('change', {
            scope: this.database[scope]
        });
    }

    clear(scope:number|string = this.selectedScope) {
        scope = scope || this.selectedScope;
        this.database[scope] = this.itemFactory({});
    }
}

/**
 * Meant to hold Lists of items for each scope
 * @events itemChanged
 */
export class ScopedList<T> extends ScopedBase<T[]> {
    // the array can be replaced with an object with the primary key for faster lookup
    protected database:{ [ id: string ]: T[] } = {};
    protected primaryKey:string = 'uuid';

    constructor(itemType:IInstantiatable, itemFactory?:Function, primaryKey?:string) {
        super(itemType, itemFactory);
        if (primaryKey)
            this.primaryKey = primaryKey;
    }

    protected autoScope(obj?:T) {
        if (obj && obj[this.primaryKey] && Object.keys(this.database).indexOf(obj[this.primaryKey]) !== -1) {
            return obj[this.primaryKey];
        }
        return this.selectedScope;
    }

    addScope(scope:number|string, value:T[] = []) {
        if (!scope)
            scope = value[this.primaryKey];
        this.database[scope] = value;
        //this[key] = this.database[key];
        this.changed('scopeAdded', {
            scopes: Object.keys(this.database)
        });

        if (!this.selectedScope)
            this.setSelectedScope(scope);
    }

    setSelectedScope(scope:number|string) {
        if (!this.database[scope]) {
            this.addScope(scope, []);
        }
        this.selectedScope = scope;
        this.changed('scopeChanged', {
            selectedScope: this.selectedScope
        });
    }

    /**
     * Get item with Primary Id
     * @param id 
     * @returns 
     */
    get(id:number|string, scope = this.selectedScope):T {
        return this.find(id, this.primaryKey, scope);
    }

    set(items:T|T[], scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        if (!this.database[scope]) {
            this.addScope(scope);
        }

        if (Array.isArray(items)) {
            this.clear(scope);
            for (let item of items) {
                this.setItem(item, scope);
            }
        } else {
            this.setItem(items, scope);
        }
    }

    setItem(item:T, scope = this.selectedScope) {
        scope = scope || this.selectedScope;
       /* if (this.itemType) {
            console.log('ScopedList instanceOf', item.constructor.name, this.itemType.constructor.name);
            console.log('ScopedList instanceOf', item instanceof this.itemType);
        } else {
            console.log('NO ITEMTYPE?', this.itemType, item.constructor.name);
        }*/
        if (!(item instanceof this.itemType) && this.itemFactory) {
            //console.log('ScopedList autoconvert', item, this.itemType);
            item = this.itemFactory(item);
        }

        let ex = this.find(item[this.primaryKey]);
        if (ex) {
            let index = this.database[scope].indexOf(ex)
            this.database[scope].splice(index, 1);
        } else {
            this.database[scope].push(item);
        }
        this.changed('itemChanged', {
            scope,
            items: [ item ]
        });
    }

    /**
     * Get item at selected index
     * @param id 
     * @returns 
     */
    getAt(id:number, scope = this.selectedScope):T {
        scope = scope || this.selectedScope;
        return this.database[scope][id];
    }

    replace(items:T[], scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        this.database[scope] = items;
        this.changed('itemChanged', {
            scope,
            items: this.database[scope]
        });
    }

    update(items:Partial<T>[], scope = this.selectedScope) {
        for (let item of items) {
            this.updateItem(item, scope);
        }
    }

    updateItem(item:Partial<T>, scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        let ex = this.find(item[this.primaryKey]);
        if (ex) {
            Object.assign(ex, item);
        } else {
            //this.push(item);
        }
        this.changed('itemChanged', {
            scope,
            items: [ item ]
        });
    }

    push(item:T, scope = this.selectedScope) {
        scope = scope || this.selectedScope;
        this.database[this.selectedScope].push(item);
        this.changed('itemChanged', {
            scope,
            items: [ item ]
        });
    }

    clear(scope:number|string = this.selectedScope) {
        scope = scope || this.selectedScope;
        this.database[scope] = [];
    }

    contains(id:string):boolean {
        return !!this.find(id);
    }

    /**
     * Find the item with the given value of a property
     * @param value Property value to look for or function to match
     * @param key (Optional, default:primary) The key to use inside items 
     * @param scope (Optional) Scope
     * @returns 
     */
    find(value:string|number|boolean|Date|Function, key: string = this.primaryKey, scope = this.selectedScope):T {
        key = key || this.primaryKey;
        scope = scope || this.selectedScope;

        return this.database[scope]
                    .find(item => {
                        if (typeof value === 'function') {
                            return value(item);
                        }
                        return subGet(item, key) === value;
                    });
    }
    
    /**
     * Filter elements of a scope by property values
     * @param value 
     * @param key 
     * @param scope 
     * @returns 
     */
    filter(value:any, key: string = this.primaryKey, scope = this.selectedScope):T[] {
        key = key || this.primaryKey;
        scope = scope || this.selectedScope;

        return this.database[scope]
                    .filter(item => {
                        if (typeof value === 'function') {
                            return value(item);
                        }
                        return subGet(item, key) === value;
                    });
    }

    /**
     * Get all items of a scope
     * @param scope 
     */
    all(scope:number|string = this.selectedScope):T[] {
        scope = scope || this.selectedScope;

        return this.database[scope] || [];
    }
}